import React, { useEffect } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Spin, message } from "antd";
import axios from "axios";
import { checkIsLoaded } from "../../lib/utils/checkLoadingStatus";
import {
  fetchAccountStatuses,
  fetchAccountTypes,
  fetchCountries,
  fetchPhoneTypes,
  fetchRegions,
  getDocTypes,
} from "../../store/slices/dicts";
import { createAccountRoute } from "../../lib/routes/accounts";
import { toHttpFormatStartOfDay, toYearMonthDayLineFormat } from "../../lib/utils/formatDate";
import AccountCreateIndividual from "../../components/AccountCreateIndividual/AccountCreateIndividual";
import { useLoading } from "../../lib/hooks/useLoading";

interface ICreateIndividualAccountParams {
  accountStatusId?: number;
  fromDate?: string;
  subscribers?: {
    externalId: string;
    subscriberTypeId: number;
  }[];
  billDeliveryEmails?: {
    email: string;
  }[];
  accTypeId?: number;
  individual?: {
    sex: string;
    firstName: string;
    lastName: string;
    middleName: string;
    birthDate?: string;
    birthPlace: string;
    registrationAddress?: {
      countryId: number;
      regionId: number;
      postCode: string;
      district: string;
      city: string;
      village: string;
      street: string;
      house: string;
      building: string;
      apartment: string;
      unstructuredAddress: string;
    };
    actualAddress?: {
      countryId: number;
      regionId: number;
      postCode: string;
      district: string;
      city: string;
      village: string;
      street: string;
      house: string;
      building: string;
      apartment: string;
      unstructuredAddress: string;
    };
    phones: {
      countryCode: string;
      phone: string;
      phoneTypeId: number;
    }[];
    identityDocuments?: {
      docTypeId: number;
      docNum: string;
      seriesNum: string;
      issueEntity: string;
      issueCountry: string;
      issueDate: string;
    }[];
  };
}

function AccountIndividualCreateContainer() {
  const dispatch = useDispatch();

  const {
    accountStatusesLoading,
    countriesLoading,
    regionsLoading,
    docTypesLoading,
    phoneTypesLoading,
    accountTypesLoading,
  } = useSelector((state: RootStateOrAny) => state.dicts);
  const { tenantPropertiesLoading } = useSelector((state: RootStateOrAny) => state.tenants);
  const history = useHistory();

  useEffect(() => {
    if (!checkIsLoaded(accountStatusesLoading)) dispatch(fetchAccountStatuses());
    if (!checkIsLoaded(countriesLoading)) dispatch(fetchCountries());
    if (!checkIsLoaded(regionsLoading)) dispatch(fetchRegions());
    if (!checkIsLoaded(docTypesLoading)) dispatch(getDocTypes());
    if (!checkIsLoaded(phoneTypesLoading)) dispatch(fetchPhoneTypes());
    if (!checkIsLoaded(accountTypesLoading)) dispatch(fetchAccountTypes());
  }, [dispatch]);

  const { isLoading, startLoading, stopLoading } = useLoading();

  const createIndividualAccount = ({ params, callback }) => {
    startLoading();
    let queryParams: ICreateIndividualAccountParams = {};
    const subscribers = [];
    const legalAddress = {
      countryId: params?.country,
      regionId: params?.region,
      postCode: params?.index || "",
      district: params?.district,
      city: params?.city,
      village: params?.village,
      street: params?.street,
      house: params?.house,
      building: params?.building,
      apartment: params?.apartment,
      unstructuredAddress: params?.unstructuredAddress,
    };
    const actualAddress = params.isSame
      ? legalAddress
      : {
          countryId: params?.actualCountry,
          regionId: params?.actualRegion,
          postCode: params?.actualIndex || "",
          district: params?.actualDistrict,
          city: params?.actualCity,
          village: params?.actualVillage,
          street: params?.actualStreet,
          house: params?.actualHouse,
          building: params?.actualBuilding,
          apartment: params?.actualApartment,
          unstructuredAddress: params?.actualUnstructuredAddress,
        };

    params.exiteId.forEach((id) => {
      if (id !== "") {
        // @ts-ignore
        subscribers.push({ externalId: id, subscriberTypeId: 1 });
      }
    });

    queryParams = {
      ...queryParams,
      accountStatusId: params.status,
      fromDate: toHttpFormatStartOfDay(params.createDate),
      subscribers,
      individual: {
        sex: params?.sex,
        firstName: params.firstName,
        lastName: params.lastName,
        middleName: params.middleName,
        birthDate: params.birthDate ? toYearMonthDayLineFormat(params.birthDate) : undefined,
        birthPlace: params?.birthPlace,
        phones: params.phones,
      },
      accTypeId: params.accTypeId,
    };

    const billDeliveryEmails = params?.email?.flatMap((email) => (email !== "" ? { email } : []));

    // Если заполнено поле в разделе "Электронная почта" значит передаём иначе не заполняли
    if (billDeliveryEmails?.length > 0 && queryParams.individual) {
      queryParams.billDeliveryEmails = billDeliveryEmails;
    }

    // Если заполнено обязательное поле в разделе "Удостоверение личности" значит передаём иначе не заполняли
    if (params?.docTypeId && queryParams.individual) {
      queryParams.individual.identityDocuments = [
        {
          docTypeId: params.docTypeId,
          docNum: params.docNum,
          seriesNum: params?.seriesNum,
          issueEntity: params.issueEntity,
          issueCountry: params.issueCountry,
          issueDate: toYearMonthDayLineFormat(params.issueDate),
        },
      ];
    }

    // Если заполнено обязательное поле в разделе "Адрес регистрации" значит передаём иначе не заполняли
    if (params?.country && queryParams.individual) {
      queryParams.individual.registrationAddress = legalAddress;
    }

    // Если заполнено обязательное поле в разделе "Фактический адрес" значит передаём иначе не заполняли
    if (params?.actualCountry && queryParams.individual) {
      queryParams.individual.actualAddress = actualAddress;
    }

    axios
      .post(createAccountRoute(), queryParams)
      .then(({ data }) => {
        callback();
        message.success("Аккаунт создан");
        history.push(`/accounts/${data.id}/info`);
      })
      .catch((error) => {
        message.error(error?.response?.data?.message);
      })
      .finally(stopLoading);
  };

  if (
    checkIsLoaded(
      accountStatusesLoading,
      countriesLoading,
      regionsLoading,
      docTypesLoading,
      tenantPropertiesLoading,
      phoneTypesLoading
    )
  ) {
    return (
      <div className="container_height" style={{ padding: 10 }}>
        <AccountCreateIndividual
          createIndividualAccount={createIndividualAccount}
          isCreateInProcess={isLoading}
        />
      </div>
    );
  }
  return (
    <div className="container_height">
      <Spin className="spin" />
    </div>
  );
}

export default AccountIndividualCreateContainer;
